/**
 * Visual pile of pending storyline events sitting BEHIND the active
 * EffectPromptDrawer card. Renders nothing when the storyline has
 * 0 or 1 events (the active drawer alone is enough).
 *
 * Layout: the active drawer anchors its card image at the right edge,
 * vertically centered. The pile mirrors that anchor and renders each
 * pending event card with progressive offset + scale + opacity so the
 * stack reads as receding "behind" the active card.
 *
 * v1.41. Phase 1 of the storyline feature. Per codex, new events
 * interrupt the storyline above the triggering event (LIFO). The
 * pile shows what will resolve NEXT after the current active prompt,
 * not what was queued earliest.
 *
 * Props:
 *   pileEvents. Array of pending events EXCLUDING the active one,
 *                ordered top-of-pile-first (i.e. the next event to
 *                resolve sits at index 0, then deeper layers behind).
 *                Each event uses the existing prompt shape
 *                ({ id, cardInstance: { isSite, imageUrl, name } }).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { GOLD, RADIUS } from '../../lib/medievalTheme';

const PORTRAIT_WIDTH_CQW  = 14;
const PORTRAIT_HEIGHT_CQW = 19;
const LANDSCAPE_WIDTH_CQW  = 19;
const LANDSCAPE_HEIGHT_CQW = 14;

// Z-index ladder: drawer is 60, vignette is 59. Pile sits at 58 so
// it draws BEHIND both. The active card occludes its top edge,
// reinforcing the depth illusion.
const Z = 58;

// Per-layer visual recession. Each pending event further back gets
// nudged upward + leftward (toward the centre of the canvas, away
// from the right-edge anchor), shrunk slightly, dimmed, and rotated
// to look like a hand-shuffled pile of cards.
const OFFSET_X_PER_LAYER_CQW = -0.9;
const OFFSET_Y_PER_LAYER_CQW = -0.9;
const SCALE_PER_LAYER = 0.965;
const OPACITY_PER_LAYER = 0.78;
const ROTATE_PER_LAYER_DEG = -1.6;

const TRANSITION = 'cubic-bezier(0.22, 0.9, 0.3, 1)';

const OPAQUE_BASE = '#0e0c08';

function cardSize(event) {
  const isSite = !!event?.cardInstance?.isSite;
  return {
    w: isSite ? LANDSCAPE_WIDTH_CQW  : PORTRAIT_WIDTH_CQW,
    h: isSite ? LANDSCAPE_HEIGHT_CQW : PORTRAIT_HEIGHT_CQW,
  };
}

function PileCard({ event, depth }) {
  const { w, h } = cardSize(event);
  const isSite = !!event.cardInstance?.isSite;
  const imageUrl = event.cardInstance?.imageUrl;
  const cardName = event.cardName || event.cardInstance?.name || '';

  const dx = depth * OFFSET_X_PER_LAYER_CQW;
  const dy = depth * OFFSET_Y_PER_LAYER_CQW;
  const scale = Math.pow(SCALE_PER_LAYER, depth);
  const opacity = Math.pow(OPACITY_PER_LAYER, depth);
  const rotate = depth * ROTATE_PER_LAYER_DEG;

  const wrapperStyle = {
    position: 'absolute',
    right: '1.5cqw',
    top: '50%',
    width: `${w}cqw`,
    height: `${h}cqw`,
    borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
    overflow: 'hidden',
    backgroundColor: OPAQUE_BASE,
    boxShadow: `0 0.5cqw 1.5cqw rgba(0,0,0,0.7), 0 0 0 1px rgba(232, 195, 110, 0.35)`,
    pointerEvents: 'none',
    willChange: 'transform, opacity',
  };

  const imgStyle = isSite ? {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: `${h}cqw`,
    height: `${w}cqw`,
    transform: 'translate(-50%, -50%) rotate(90deg)',
    transformOrigin: 'center center',
    objectFit: 'cover',
  } : {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  return (
    <motion.div
      key={event.id}
      initial={{
        opacity: 0,
        x: `${dx + 3}cqw`,
        y: `calc(-50% + ${dy - 2}cqw)`,
        scale: scale * 0.92,
        rotate: rotate + 6,
      }}
      animate={{
        opacity,
        x: `${dx}cqw`,
        y: `calc(-50% + ${dy}cqw)`,
        scale,
        rotate,
      }}
      exit={{
        opacity: 0,
        x: `${dx - 2}cqw`,
        y: `calc(-50% + ${dy + 4}cqw)`,
        scale: scale * 0.94,
        rotate: rotate - 4,
        transition: { duration: 0.18, ease: TRANSITION },
      }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      style={wrapperStyle}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={cardName} draggable={false} style={imgStyle} />
      ) : (
        <div
          style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: GOLD, fontSize: '1cqw', textAlign: 'center', padding: '0.5cqw',
          }}
        >
          {cardName}
        </div>
      )}
    </motion.div>
  );
}

export default function StorylinePile({ pileEvents }) {
  if (!pileEvents || pileEvents.length === 0) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z,
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence initial={false}>
        {pileEvents.map((event, idx) => (
          // depth=1 means "just behind the active card." The active
          // EffectPromptDrawer sits at depth 0 (its own layout); the
          // first pile entry is depth 1 so it peeks out from behind.
          <PileCard key={event.id} event={event} depth={idx + 1} />
        ))}
      </AnimatePresence>
    </div>
  );
}
